import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Loader2, AlertTriangle, Inbox } from "lucide-react";
import { useMemo } from "react";
import { useDatasetExplorerStore } from "../datasetExplorerStore";
import { parseCsv } from "@forgepoint/shared";

/**
 * Formats a numeric value for display with appropriate precision.
 * @param value - The numeric value to format
 * @returns Formatted string representation
 */
function formatNumeric(value: number): string {
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/**
 * Dynamic results table that renders columns and rows from a DatasetResult.
 * Uses raw shadcn Table primitives (not DataTable) because columns are dynamic.
 */
export default function ResultsTable() {
  const result = useDatasetExplorerStore((s) => s.result);
  const resultLoading = useDatasetExplorerStore((s) => s.resultLoading);
  const resultError = useDatasetExplorerStore((s) => s.resultError);

  const parsed = useMemo(
    () => (result ? parseCsv(result.csv) : null),
    [result],
  );

  if (resultLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (resultError) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {resultError}
      </div>
    );
  }

  if (!result || !parsed) {
    return null;
  }

  if (result.rowCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
        <Inbox className="h-8 w-8 opacity-30 animate-gentle-float" />
        <p className="text-sm">No results found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {result.truncated && (
        <div className="flex items-center gap-2 rounded-md border border-yellow-500/30 bg-yellow-500/5 px-3 py-2 text-xs text-yellow-700 dark:text-yellow-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Results truncated to {result.rowCount} rows. Add filters to narrow
          your query.
        </div>
      )}

      <ScrollArea className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              {result.columns.map((col) => (
                <TableHead
                  key={col}
                  className="whitespace-nowrap text-xs font-semibold h-9"
                >
                  {col}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {parsed.rows.map((row, rowIdx) => (
              <TableRow
                key={rowIdx}
                className={rowIdx % 2 === 1 ? "bg-muted/20" : ""}
              >
                {parsed.columns.map((col) => {
                  const value = row[col];
                  const numericValue = Number(value);
                  const isNumeric = value !== "" && !isNaN(numericValue);

                  return (
                    <TableCell
                      key={col}
                      className={`whitespace-nowrap text-xs py-2 ${
                        isNumeric ? "text-right tabular-nums font-mono" : ""
                      } ${value === "" ? "text-muted-foreground/40" : ""}`}
                    >
                      {value === ""
                        ? "\u2014"
                        : isNumeric
                          ? formatNumeric(numericValue)
                          : value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}
